import CoreGeneralQuestionUI, { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from '../Components/ui/react/core-general-question-ui';
import GameManager from '../GameManager';
import { coreConstantsGeneralUIs } from '../Misc/CoreConstants';
import UIManager, { UI, UIInstance } from './UiManager';
import React from 'react';
import { SingleUI } from '../Components/ui/react/base/SingleUI';
import CoreGeneralFeedbackUI, { CoreGeneralFeedbackUIConfig } from '../Components/ui/react/core-general-feedback-ui';
import CoreGeneralAARUI, { GeneralAARUIConfig } from '../Components/ui/react/core-general-aar-ui';

/**
 * Manages the UI for a JSX-type framework
 */
export default class JsxUIManager extends UIManager {
	/**
	 * ctor
	 * @param gameManager A reference to the Game Manager
	 */
	constructor(gameManager: GameManager) {
		super(gameManager)
	}

	/**
	 * Registers a single UI
	 * @param uiName The name of the UI to register
	 * @param uiData Data to append to the UI
	 * @param tag Optional: Use this to group UIs. Example: 'HUD'
	 * @returns true if it was successfully registered
	 */
	public registerUI(uiName: string, uiData: JSXUIData, tag?: string): boolean {
		let result = super.registerUI(uiName, uiData, tag);
		return result;
	}

	/**
	 * Register all general uis to use for the game
	 */
	public registerGeneralUIs(): void {
		this.registerUI(coreConstantsGeneralUIs.CoreGeneralQuestion, {
			debugUITemplate: [
				{
					name: 'Single Choice',
					debugUITemplate: () => {
						return {
							title: "Debug Scenario",
							multipleChoice: false,
							percentage: 0.2,
							subtitle: "Debug Step",
							questionTitle: 'Which option is the best to begin the meeting with the employee?',
							options: [
								{
									title: 'Option 1',
									description: 'Hi Jared, thank you for meeting with me. Why did the patient fall?',
									feedbackHtml: `
											<p class="default-text">This response is not the best because asking the question in this way creates an environment of looking for blame. 
											Asking what happened is the first step. </p>
										`,
									isCorrect: false,
									score: 0
								},
								{
									title: 'Option 2',
									description: 'Hi Jared, thank you for meeting with me. You know we’re focused on zero harm. Why did your patient fall?',
									feedbackHtml: `
											<p class="default-text">This response is not the best because asking the question in this way creates an environment of looking for blame. 
											Asking what happened is the first step. </p>
										`,
									isCorrect: false,
									score: 0
								},
								{
									title: 'Option 3',
									description: 'Hi Jared, I’m assuming you know why I called to meet you.',
									feedbackHtml: `
											<p class="default-text">This response is not the best because it lacks showing respect for the employee and is not the best for 
											establishing psychological safety.</p>
										`,
									isCorrect: false,
									score: 0
								},
								{
									title: 'Option 4',
									description: 'Hi Jared, thank you for meeting with me. I understand there was a patient fall. Can you tell me what happened?',
									feedbackHtml: `
											<p class="default-text">Best answer. This response shows respect to the employee by thanking them for meeting with you, asking how they 
											are doing demonstrates your concern for them as a second victim. Asking “what” happened versus “why” the incident occurred 
											opens the dialogue and reinforces a culture of safety.</p>
										`,
									isCorrect: true,
									score: 5
								}
							],

							onSubmitClicked: () => {

							}
						} as GeneralQuestionUIConfig
					}
				},
				{
					name: 'Multiple Choice',
					debugUITemplate: () => {
						return {
							title: "Sample Scenario",
							multipleChoice: true,
							percentage: 0.2,
							subtitle: "Sample Step",
							questionTitle: 'Which option is the best to begin the meeting with the employee?',
							options: [
								{
									title: 'Option 1',
									description: 'Hi Jared, thank you for meeting with me. Why did the patient fall?',
									feedbackHtml: `
											<p class="default-text">This response is not the best because asking the question in this way creates an environment of looking for blame. 
											Asking what happened is the first step. </p>
										`,
									isCorrect: true,
									score: 5
								},
								{
									title: 'Option 2',
									description: 'Hi Jared, thank you for meeting with me. You know we’re focused on zero harm. Why did your patient fall?',
									feedbackHtml: `
											<p class="default-text">This response is not the best because asking the question in this way creates an environment of looking for blame. 
											Asking what happened is the first step. </p>
										`,
									isCorrect: false,
									score: 0
								},
								{
									title: 'Option 3',
									description: 'Hi Jared, I’m assuming you know why I called to meet you.',
									feedbackHtml: `
											<p class="default-text">This response is not the best because it lacks showing respect for the employee and is not the best for 
											establishing psychological safety.</p>
										`,
									isCorrect: false,
									score: 0
								},
								{
									title: 'Option 4',
									description: 'Hi Jared, thank you for meeting with me. I understand there was a patient fall. Can you tell me what happened?',
									feedbackHtml: `
											<p class="default-text">Best answer. This response shows respect to the employee by thanking them for meeting with you, asking how they 
											are doing demonstrates your concern for them as a second victim. Asking “what” happened versus “why” the incident occurred 
											opens the dialogue and reinforces a culture of safety.</p>
										`,
									isCorrect: true,
									score: 5
								}
							],

							onSubmitClicked: () => {

							}
						} as GeneralQuestionUIConfig
					}
				}
			],
			renderMethod: (instance: any) => {
				return <CoreGeneralQuestionUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralQuestionUI>
			}
		});
		this.registerUI(coreConstantsGeneralUIs.CoreGeneralFeedback, {
			debugUITemplate: [
				{
					name: 'Default',
					debugUITemplate: () => {
						return {
							onButtonClicked: () => {

							},
							selectedOption: {
								title: 'Option 4',
								description: 'Hi Jared, thank you for meeting with me. I understand there was a patient fall. Can you tell me what happened?',
								feedbackHtml: `
									<p class="default-text">Best answer. This response shows respect to the employee by thanking them for meeting with you, asking how they 
									are doing demonstrates your concern for them as a second victim. Asking “what” happened versus “why” the incident occurred 
									opens the dialogue and reinforces a culture of safety.</p>
								`,
								isCorrect: true,
								score: 5
							},
							title: 'Debug Scenario'
						} as CoreGeneralFeedbackUIConfig
					}
				}
			],
			renderMethod: (instance: any) => {
				return <CoreGeneralFeedbackUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralFeedbackUI>
			}
		});

		this.registerUI(coreConstantsGeneralUIs.CoreGeneralAAR, {
			debugUITemplate: [
				{
					name: 'Default',
					debugUITemplate: () => {
						return {
							entries: [
								{
									feedback: `<p class="default-text">Best answer. This response shows respect to the employee by thanking them for meeting with you, asking how they 
									are doing demonstrates your concern for them as a second victim. Asking “what” happened versus “why” the incident occurred 
									opens the dialogue and reinforces a culture of safety.</p>`,
									isCorrect: true,
									questionTitle: 'Which option is the best to begin the meeting with the employee?',
									scoreAwarded: 5,
									scorePossible: 5,
									selectedOption: 'Option 4: Hi Jared, thank you for meeting with me. I understand there was a patient fall. Can you tell me what happened?'
								},
								{
									feedback: `
										<ul>
											<li class="default-text">The employee is accountable and should receive coaching.</li>
											<li class="default-text">The employee may participate in teaching others the lessons learned.</li>
										</ul>
									`,
									isCorrect: true,
									questionTitle: 'What best describes the employee\'s action?',
									scoreAwarded: 5,
									scorePossible: 5,
									selectedOption: 'Option 4: The employee made a potentially unsafe choice. Faulty or self-serving decision making may be evident, or short cuts, or routine rule violations.'
								},
								{
									feedback: `
										<p class="default-text">waiting for final content</p>
									`,
									isCorrect: true,
									questionTitle: 'Would 3 other employees with similar skills and knowledge do the same thing in similar circumstances?',
									scoreAwarded: 5,
									scorePossible: 5,
									selectedOption: 'No'
								},
								{
									feedback: `
										<p class="default-text">waiting for final content</p>
									`,
									isCorrect: true,
									questionTitle: 'Are there repeated errors, or occurrences of at-risk behavior?',
									scoreAwarded: 5,
									scorePossible: 5,
									selectedOption: 'Yes'
								},
								{
									feedback: `
										<p class="default-text">waiting for final content</p>
									`,
									isCorrect: false,
									questionTitle: 'If YES, what action will you take?',
									scoreAwarded: 0,
									scorePossible: 5,
									selectedOption: 'Option 2: Coach employee'
								}
							],
							onCloseClicked: () => {
		
							},
							onGetIfPassed: () => {
								return true;
							}
						} as GeneralAARUIConfig
					}
				}
			],
			renderMethod: (instance: any) => {
				return <CoreGeneralAARUI id={instance.id} key={instance.id} parameters={instance.parameters}></CoreGeneralAARUI>
			}
		});
	}

	/**
	 * Renders all single UIs
	 */
	public renderSingleGeneralUIs(): any {
		return (
			<div>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralQuestion} uiManager={this}></SingleUI>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralFeedback} uiManager={this}></SingleUI>
				<SingleUI id={coreConstantsGeneralUIs.CoreGeneralAAR} uiManager={this}></SingleUI>
			</div>
		)
	}

	/**
	 * Calls renderMethod in all UIs.
	 * @returns The html to render
	 */
	public render(): any {
		let result = [];
		for (let uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			let ui = this._uis[uiIndx];

			for (let uiInstanceIndx = 0; uiInstanceIndx < ui.instances.length; uiInstanceIndx++) {
				let instance = ui.instances[uiInstanceIndx];
				let res = this._renderUiInstance(ui, instance);
				if (res) {
					result.push(res);
				}
			}
		}

		return result;
	}

	/**
	 * Renders a single instance (if it should be rendered)
	 * @param ui The instance of the UI
	 * @param instance The instance itself
	 * @returns The html to render for a single instance
	 */
	private _renderUiInstance(ui: UI, instance: UIInstance): any {
		return instance.parameters.isVisible && ui.uiData.renderMethod ?
			ui.uiData.renderMethod(instance)
			: null;
	}
}

export interface JSXUIData {
	/**
	 * A sample configuration of what the UI will have. Only used for debug purposes
	 */
	debugUITemplate?: JSXUIDebugTemplate[];

	/**
	 * Called for every instance of this UI when it needs to be rendered
	 */
	renderMethod: (uiInstance: any) => any;
}

export interface JSXUIDebugTemplate {
	name: string;
	debugUITemplate: () => any;
}